import React from 'react';

import DownloadIcon from '@mui/icons-material/Download';

import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

const MONTH_COLUMNS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function formatCurrency(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '$0.00';
  }

  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

function formatInteger(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '0';
  }

  return number.toLocaleString('en-US');
}

function valueForCell(value, asCurrency) {
  return asCurrency ? formatCurrency(value) : formatInteger(value);
}

function formatYear(value) {
  const year = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(year) ? String(year) : '';
}

function TZMMSActivityTable({
  title,
  rows,
  showPaymentMethod,
  asCurrency,
  footerTotals,
  onExport,
  exportLabel = 'Export CSV',
}) {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5 }}
      >
        <Typography variant="h6">{title}</Typography>
        {onExport ? (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={onExport}
          >
            {exportLabel}
          </Button>
        ) : null}
      </Stack>
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table size="small" aria-label={title}>
          <TableHead>
            <TableRow>
              <TableCell>Division</TableCell>
              <TableCell>REP</TableCell>
              <TableCell>2ndRep</TableCell>
              <TableCell>3rdRep</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Facility</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>State</TableCell>
              <TableCell align="right">Year</TableCell>
              {MONTH_COLUMNS.map((month) => (
                <TableCell key={month} align="right">
                  {month}
                </TableCell>
              ))}
              <TableCell align="right">Total</TableCell>
              {showPaymentMethod ? <TableCell>Payment Method</TableCell> : null}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow
                key={`${row.Code}-${row.Service}-${row.Year}-${rowIndex}`}
                hover
                sx={{ '& td': { whiteSpace: 'nowrap' } }}
              >
                <TableCell>{row.Division}</TableCell>
                <TableCell>{row.REP}</TableCell>
                <TableCell>{row['2ndRep']}</TableCell>
                <TableCell>{row['3rdRep']}</TableCell>
                <TableCell>{row.Code}</TableCell>
                <TableCell>{row.Facility}</TableCell>
                <TableCell>{row.Service}</TableCell>
                <TableCell>{row.State}</TableCell>
                <TableCell align="right">{formatYear(row.Year)}</TableCell>
                {MONTH_COLUMNS.map((month) => (
                  <TableCell key={month} align="right">
                    {valueForCell(row[month], asCurrency)}
                  </TableCell>
                ))}
                <TableCell align="right">{valueForCell(row.Total, asCurrency)}</TableCell>
                {showPaymentMethod ? <TableCell>{row.PaymentMethod}</TableCell> : null}
              </TableRow>
            ))}
          </TableBody>

          {footerTotals ? (
            <TableFooter>
              <TableRow sx={{ '& td': { fontWeight: 600, whiteSpace: 'nowrap' } }}>
                <TableCell colSpan={9}>Totals</TableCell>
                {MONTH_COLUMNS.map((month) => (
                  <TableCell key={month} align="right">
                    {valueForCell(footerTotals[month], asCurrency)}
                  </TableCell>
                ))}
                <TableCell align="right">{valueForCell(footerTotals.Total, asCurrency)}</TableCell>
                {showPaymentMethod ? <TableCell /> : null}
              </TableRow>
            </TableFooter>
          ) : null}
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default React.memo(TZMMSActivityTable);
